import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import Home from './pages/Home';
import SettingsLayout from './pages/SettingsLayout';
import SettingsGeneral from './pages/SettingsGeneral';
import SettingsBilling from './pages/SettingsBilling';
import SettingsUsers from './pages/SettingsUsers';
import SettingsClients from './pages/SettingsClients';
import SettingsAdmin from './pages/SettingsAdmin';
import SettingsRBAC from './pages/SettingsRBAC';
import SettingsAuditLog from './pages/SettingsAuditLog';
import SettingsMetrics from './pages/SettingsMetrics';
import SettingsEmbeddings from './pages/SettingsEmbeddings';
import SettingsSubscriptions from './pages/SettingsSubscriptions';
import Projects from './pages/Projects';
import SemanticSearchPage from './pages/SemanticSearchPage';
import CpcSearchPage from './pages/CpcSearchPage';
import SearchTranscript from './pages/SearchTranscript';
import KnowledgeBases from './pages/KnowledgeBases';
import Users from './pages/Users';
import Clients from './pages/Clients';
import Profile from './pages/Profile';
import AdminTokenUsage from './pages/AdminTokenUsage';
import AdminPromptsPage from './pages/AdminPromptsPage';
const DashboardOverview = React.lazy(() => import('./pages/DashboardOverview'));
const DashboardCosts = React.lazy(() => import('./pages/DashboardCosts'));
const DashboardActivity = React.lazy(() => import('./pages/DashboardActivity'));
const AdminCostAnalysis = React.lazy(() => import('./pages/AdminCostAnalysis'));
import SpeakingChatbotV2 from './pages/SpeakingChatbotV2';
import WorkspacePage from './pages/WorkspacePage';
import FullKBReviewProgress from './pages/FullKBReviewProgress';
const KBRagStatusPage = React.lazy(() => import('./pages/KBRagStatusPage'));
import Watches from './pages/Watches';
import Help from './pages/Help';
import ActorLandscape from './pages/ActorLandscape';
import LandscapeReportPage from './pages/LandscapeReportPage';
import OpsRetrievalProgress from './pages/OpsRetrievalProgress';
import OpenalexRetrievalProgress from './pages/OpenalexRetrievalProgress';
import './App.css';
import { ToastProvider } from './components/ToastProvider';
const ComponentShowcase = import.meta.env.DEV
  ? React.lazy(() => import('./dev/ComponentShowcase'))
  : null;

// Redirect wrappers for backwards compatibility
const ProjectRedirect: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/scv2/project/${projectId}`} replace />;
};

const SubProjectRedirect: React.FC = () => {
  const { subProjectId } = useParams<{ subProjectId: string }>();
  return <Navigate to={`/scv2/sub-project/${subProjectId}`} replace />;
};

const KnowledgeBaseRedirect: React.FC = () => {
  const { kbId } = useParams<{ kbId: string }>();
  return <Navigate to={`/scv2/kb/${kbId}`} replace />;
};

const ClaimsWorkspaceRedirect: React.FC = () => {
  const { subProjectId } = useParams<{ subProjectId: string }>();
  const [searchParams] = useSearchParams();
  const solutionId = searchParams.get('solutionId');
  const params = new URLSearchParams();
  if (solutionId) params.set('solutionId', solutionId);
  return <Navigate to={`/workspace/${subProjectId}/claims?${params.toString()}`} replace />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router>
          <Routes>
            {/* Public route */}
            <Route path="/login" element={<Login />} />

            {/* Standalone landscape report — no sidebar, opens in new tab */}
            <Route path="/landscape-report/:landscapeId" element={<LandscapeReportPage />} />

            {/* Dev-only showcase */}
            {ComponentShowcase && (
              <Route
                path="/dev/showcase"
                element={
                  <React.Suspense fallback={null}>
                    <ComponentShowcase />
                  </React.Suspense>
                }
              />
            )}

            {/* All protected routes share AppLayout (sidebar + Outlet) */}
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/project/:projectId" element={<ProjectRedirect />} />
              <Route path="/sub-project/:subProjectId" element={<SubProjectRedirect />} />
              <Route path="/semantic-search/:subProjectId" element={<SemanticSearchPage />} />
              <Route path="/search-transcript/:transcriptId" element={<SearchTranscript />} />
              <Route path="/full-kb-review/:subProjectId/:reviewId" element={<FullKBReviewProgress />} />
              <Route path="/ops-retrieval-progress/:jobId" element={<OpsRetrievalProgress />} />
              <Route path="/openalex-retrieval-progress/:jobId" element={<OpenalexRetrievalProgress />} />
              <Route path="/kb-rag-status/:kbId" element={<React.Suspense fallback={null}><KBRagStatusPage /></React.Suspense>} />
              <Route path="/knowledge-bases" element={<KnowledgeBases />} />
              <Route path="/knowledge-base/:kbId" element={<KnowledgeBaseRedirect />} />
              <Route path="/watches" element={<Watches />} />
              <Route path="/help" element={<Help />} />
              <Route path="/actor-landscape/:kbId" element={<ActorLandscape />} />

              {/* Settings with nested routes */}
              <Route path="/settings" element={<SettingsLayout />}>
                <Route path="general" element={<SettingsGeneral />} />
                <Route path="billing" element={<SettingsBilling />} />
                <Route path="subscriptions" element={<SettingsSubscriptions />} />
                <Route path="users" element={<SettingsUsers />} />
                <Route path="clients" element={<SettingsClients />} />
                <Route path="admin" element={<SettingsAdmin />} />
                <Route path="rbac" element={<SettingsRBAC />} />
                <Route path="audit-log" element={<SettingsAuditLog />} />
                <Route path="metrics" element={<SettingsMetrics />} />
                <Route path="embeddings" element={<SettingsEmbeddings />} />
              </Route>

              {/* Analytics (SysAdmin / DevOps only) */}
              <Route path="/analytics" element={<React.Suspense fallback={null}><DashboardOverview /></React.Suspense>} />
              <Route path="/analytics/costs" element={<React.Suspense fallback={null}><DashboardCosts /></React.Suspense>} />
              <Route path="/analytics/activity" element={<React.Suspense fallback={null}><DashboardActivity /></React.Suspense>} />

              <Route path="/users" element={<Users />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/admin/token-usage" element={<AdminTokenUsage />} />
              <Route path="/admin/prompts" element={<AdminPromptsPage />} />
              <Route path="/admin/cost-analysis" element={<React.Suspense fallback={null}><AdminCostAnalysis /></React.Suspense>} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/scv2/:entityType/:entityId" element={<SpeakingChatbotV2 />} />
              <Route path="/workspace/:subProjectId/:field" element={<WorkspacePage />} />
              <Route path="/claims-workspace/:subProjectId" element={<ClaimsWorkspaceRedirect />} />
              <Route path="/cpc-search/:subProjectId" element={<CpcSearchPage />} />
            </Route>
          </Routes>
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
